/**
 * AuthModal — Modal-based auth for conversion flow.
 *
 * Replaces redirect to /login with an inline modal so the user stays on the
 * dashboard. Supports Google OAuth (primary) + email/password (fallback).
 *
 * After successful auth, calls onSuccess() so the parent can auto-proceed
 * to checkout / wizard.
 */

import React, { useState, useEffect } from "react";
import { X, Eye, EyeOff, AlertCircle, Loader2, Mail, CheckCircle2 } from "lucide-react";
import clsx from "clsx";
import { useAuthContext } from "../../contexts/AuthContext";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after successful sign-in / sign-up so parent can proceed. */
  onSuccess?: () => void;
  /** Initial mode — default "signup" for new users at conversion point. */
  initialMode?: AuthMode;
  /**
   * URL to redirect to after Google OAuth completes (via /auth/callback).
   * If not provided, the current page URL is used as the default.
   * Set this when you want post-OAuth redirect to a specific route
   * (e.g., "/dashboard" from the nav, "/wizard?address=..." from the dashboard).
   */
  oauthReturnUrl?: string;
  /** Custom heading for signup mode (overrides default "Create your account to continue") */
  signupHeading?: string;
  /** Custom subtitle for signup mode (overrides default "Save your progress...") */
  signupSubtitle?: string;
}

type AuthMode = "signup" | "login" | "forgot";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AuthModal({
  isOpen,
  onClose,
  onSuccess,
  initialMode = "signup",
  oauthReturnUrl,
  signupHeading,
  signupSubtitle,
}: AuthModalProps) {
  const {
    isAuthenticated,
    signIn,
    signUp,
    signInWithGoogle,
    resetPassword,
    error: authHookError,
  } = useAuthContext();

  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // ── Auto-close & callback on auth success ────────────────────────────
  useEffect(() => {
    if (isAuthenticated && isOpen && !needsConfirmation) {
      onSuccess?.();
      onClose();
    }
  }, [isAuthenticated, isOpen, needsConfirmation, onSuccess, onClose]);

  // ── Reset form on mode change ────────────────────────────────────────
  useEffect(() => {
    setFormError(null);
    setEmailError(null);
    setPasswordError(null);
    setNeedsConfirmation(false);
    setResetSent(false);
  }, [mode]);

  // ── Reset on open/close ──────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      setEmail("");
      setPassword("");
      setFullName("");
      setShowPassword(false);
      setIsSubmitting(false);
      setIsGoogleLoading(false);
      setFormError(null);
      setEmailError(null);
      setPasswordError(null);
      setNeedsConfirmation(false);
      setResetSent(false);
    }
  }, [isOpen, initialMode]);

  if (!isOpen) return null;

  // ── Validation helpers ───────────────────────────────────────────────

  const validateEmail = (val: string): string | null => {
    if (!val.trim()) return "Email is required.";
    if (!val.includes("@") || !val.includes(".")) return "Please enter a valid email address.";
    return null;
  };

  const validatePassword = (val: string): string | null => {
    if (!val) return "Password is required.";
    if (val.length < 6) return "Password must be at least 6 characters.";
    return null;
  };

  // ── Google OAuth ─────────────────────────────────────────────────────

  const handleGoogle = async () => {
    setIsGoogleLoading(true);
    setFormError(null);

    // If the caller specified a post-OAuth return URL, store it BEFORE the
    // redirect so /auth/callback knows where to send the user.
    if (oauthReturnUrl) {
      // Convert relative paths to full URLs for consistency
      const fullUrl = oauthReturnUrl.startsWith("http")
        ? oauthReturnUrl
        : `${window.location.origin}${oauthReturnUrl}`;
      localStorage.setItem("fp_auth_redirect", fullUrl);
    }

    const { error } = await signInWithGoogle();

    if (error) {
      setFormError(error);
      setIsGoogleLoading(false);
    }
    // On success, browser redirects to Google — loading state stays until nav.
  };

  // ── Email/Password submit ────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Validate
    const eErr = validateEmail(email);
    const pErr = mode !== "forgot" ? validatePassword(password) : null;
    setEmailError(eErr);
    setPasswordError(pErr);

    if (eErr || pErr) return;

    setIsSubmitting(true);

    try {
      if (mode === "forgot") {
        const { error } = await resetPassword(email.trim());
        if (error) {
          setFormError(error);
        } else {
          setResetSent(true);
        }
      } else if (mode === "signup") {
        const { error, needsConfirmation: confirms } = await signUp(
          email.trim(),
          password,
          fullName.trim() || undefined
        );
        if (error) {
          setFormError(error);
        } else if (confirms) {
          setNeedsConfirmation(true);
        }
        // If neither error nor confirmation, onAuthStateChange fires →
        // isAuthenticated becomes true → useEffect above calls onSuccess & closes.
      } else {
        // login
        const { error } = await signIn(email.trim(), password);
        if (error) {
          setFormError(error);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
  };

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center md:p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-fp-navy-900/50 backdrop-blur-[4px]"
        onClick={onClose}
      />

      {/* Modal Card */}
      <div className="relative w-full h-full md:h-auto md:max-w-[440px] bg-white md:rounded-2xl shadow-[0_20px_60px_rgba(15,23,42,0.20)] p-8 animate-in fade-in zoom-in-95 duration-200 overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-fp-slate-500 hover:text-fp-slate-700 rounded-full hover:bg-slate-50 transition-colors"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {/* ── Email confirmation success ─────────────────────────────── */}
        {needsConfirmation && (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-[24px] font-bold text-fp-navy-900 mb-2">
              Check your email
            </h2>
            <p className="text-fp-slate-600 mb-6">
              We sent a confirmation link to <strong>{email}</strong>. Click the
              link to activate your account, then return here.
            </p>
            <button
              onClick={() => {
                setNeedsConfirmation(false);
                switchMode("login");
              }}
              className="text-fp-teal-700 font-medium hover:underline"
            >
              Back to log in
            </button>
          </div>
        )}

        {/* ── Password reset sent ────────────────────────────────────── */}
        {resetSent && (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-[24px] font-bold text-fp-navy-900 mb-2">
              Reset link sent
            </h2>
            <p className="text-fp-slate-600 mb-6">
              Check <strong>{email}</strong> for a password reset link.
            </p>
            <button
              onClick={() => switchMode("login")}
              className="text-fp-teal-700 font-medium hover:underline"
            >
              Back to log in
            </button>
          </div>
        )}

        {/* ── Main auth form ─────────────────────────────────────────── */}
        {!needsConfirmation && !resetSent && (
          <>
            {/* Header */}
            <div className="text-center mb-6">
              <h2 className="text-[24px] font-bold text-fp-navy-900 mb-2">
                {mode === "signup"
                  ? (signupHeading || "Create your account to continue")
                  : mode === "login"
                  ? "Welcome back"
                  : "Reset your password"}
              </h2>
              {mode === "signup" && (
                <p className="text-[16px] text-fp-slate-600">
                  {signupSubtitle || "Save your progress and get your appeal packet."}
                </p>
              )}
              {mode === "forgot" && (
                <p className="text-[16px] text-fp-slate-600">
                  Enter your email and we'll send a reset link.
                </p>
              )}
            </div>

            {/* Google OAuth (not shown on forgot password) */}
            {mode !== "forgot" && (
              <>
                <button
                  onClick={handleGoogle}
                  disabled={isGoogleLoading}
                  className="w-full h-12 bg-white border border-fp-slate-300 rounded-lg flex items-center justify-center gap-3 hover:bg-fp-slate-100 transition-colors mb-6 group disabled:opacity-60"
                >
                  {isGoogleLoading ? (
                    <Loader2 className="w-5 h-5 text-fp-slate-400" />
                  ) : (
                    <svg className="w-6 h-6" viewBox="0 0 24 24">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.26.81-.58z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                  )}
                  <span className="text-[16px] font-medium text-fp-slate-700 group-hover:text-fp-navy-900">
                    Continue with Google
                  </span>
                </button>

                {/* Divider */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="h-[1px] bg-fp-slate-300 flex-1" />
                  <span className="text-[14px] text-fp-slate-500">or</span>
                  <div className="h-[1px] bg-fp-slate-300 flex-1" />
                </div>
              </>
            )}

            {/* General form error */}
            {(formError || authHookError) && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-[14px] text-red-700">
                  {formError || authHookError}
                  {formError?.includes("already exists") && mode === "signup" && (
                    <>
                      {" "}
                      <button
                        type="button"
                        onClick={() => switchMode("login")}
                        className="text-fp-teal-700 hover:underline font-medium"
                      >
                        Log in instead?
                      </button>
                    </>
                  )}
                </p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Full Name (signup only) */}
              {mode === "signup" && (
                <div>
                  <label className="block text-[14px] font-medium text-fp-slate-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full h-12 px-3 rounded-lg border border-fp-slate-300 outline-none transition-all text-base focus:border-fp-teal-700 focus:ring-1 focus:ring-fp-teal-700"
                    placeholder="Jane Doe"
                    autoComplete="name"
                  />
                </div>
              )}

              {/* Email */}
              <div>
                <label className="block text-[14px] font-medium text-fp-slate-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(null);
                  }}
                  onBlur={() => setEmailError(validateEmail(email))}
                  className={clsx(
                    "w-full h-12 px-3 rounded-lg border outline-none transition-all text-base",
                    emailError
                      ? "border-fp-red-600 focus:ring-1 focus:ring-fp-red-600"
                      : "border-fp-slate-300 focus:border-fp-teal-700 focus:ring-1 focus:ring-fp-teal-700"
                  )}
                  placeholder="you@example.com"
                  autoComplete="email"
                />
                {emailError && (
                  <p className="mt-1 text-[12px] text-fp-red-600 flex items-center gap-1">
                    <AlertCircle size={12} /> {emailError}
                  </p>
                )}
              </div>

              {/* Password (not shown on forgot) */}
              {mode !== "forgot" && (
                <div>
                  <label className="block text-[14px] font-medium text-fp-slate-700 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (passwordError) setPasswordError(null);
                      }}
                      onBlur={() => setPasswordError(validatePassword(password))}
                      className={clsx(
                        "w-full h-12 pl-3 pr-10 rounded-lg border outline-none transition-all text-base",
                        passwordError
                          ? "border-fp-red-600 focus:ring-1 focus:ring-fp-red-600"
                          : "border-fp-slate-300 focus:border-fp-teal-700 focus:ring-1 focus:ring-fp-teal-700"
                      )}
                      placeholder="••••••••"
                      autoComplete={mode === "signup" ? "new-password" : "current-password"}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-fp-slate-400 hover:text-fp-slate-500"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {passwordError && (
                    <p className="mt-1 text-[12px] text-fp-red-600 flex items-center gap-1">
                      <AlertCircle size={12} /> {passwordError}
                    </p>
                  )}

                  {/* Forgot password link (login only) */}
                  {mode === "login" && (
                    <div className="mt-2 text-right">
                      <button
                        type="button"
                        onClick={() => switchMode("forgot")}
                        className="text-[14px] text-fp-teal-700 hover:underline font-medium"
                      >
                        Forgot password?
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Submit CTA */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 bg-fp-emerald-500 text-white font-semibold rounded-lg hover:bg-fp-emerald-600 transition-colors mt-2 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {isSubmitting ? (
                  <Loader2 className="w-5 h-5" />
                ) : mode === "signup" ? (
                  "Create Account"
                ) : mode === "login" ? (
                  "Log In"
                ) : (
                  "Send Reset Link"
                )}
              </button>
            </form>

            {/* Toggle mode */}
            <div className="mt-6 text-center">
              {mode === "forgot" ? (
                <button
                  onClick={() => switchMode("login")}
                  className="text-[14px] text-fp-teal-700 font-medium hover:underline"
                >
                  Back to log in
                </button>
              ) : (
                <p className="text-[14px] text-fp-slate-700">
                  {mode === "signup"
                    ? "Already have an account?"
                    : "Don't have an account?"}{" "}
                  <button
                    onClick={() =>
                      switchMode(mode === "signup" ? "login" : "signup")
                    }
                    className="text-fp-teal-700 font-medium hover:underline"
                  >
                    {mode === "signup" ? "Log in" : "Sign up"}
                  </button>
                </p>
              )}
            </div>

            {/* Legal disclaimer (signup only) */}
            {mode === "signup" && (
              <div className="mt-6 text-center">
                <p className="text-[12px] text-fp-slate-500 leading-snug">
                  By continuing, you agree to our{" "}
                  <a
                    href="/terms"
                    className="text-fp-teal-700 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    href="/privacy"
                    className="text-fp-teal-700 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
